import {
	Box,
	Center,
	Container,
	Flex,
	Group,
	Image,
	Indicator,
	Select,
	Skeleton,
	Stack,
	Text
} from "@mantine/core";
import { useSessionStorage } from "@mantine/hooks";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import moment from "moment";
import { motion } from "motion/react";
import { parseAsString, useQueryState } from "nuqs";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Spinner } from "reactstrap";
import Swal from "sweetalert2";

import { ThemedTitle, TitleContainer } from "@/components/HeaderContainer";
import { LightThemedButton, ThemedButton } from "@/components/ThemedButton";

import generalApi from "@/services/general-api";

import { openConfirmModal } from "@mantine/modals";
import { PDF_ASPECT_RATIO } from "./components/CertPreview";

export default function CreatePage() {
	const navigate = useNavigate();
	const bundle_id = useQueryState("bundle_id", parseAsString);
	const [viewFolderModal, setViewFolderModal] = useState(false);
	const [sessionkey, setSessionKey] = useQueryState("session-key", parseAsString);
	const [selectedCount, setSelectedCount] = useSessionStorage({
		key: "professional-cert-create-selected-count" + bundle_id,
		defaultValue: 0,
	});
	const [selectedCourses, setSelectedCourses] = useSessionStorage({
		key: "professiona-cert-create-course-selection" + bundle_id,
		defaultValue: [],
	});

	const query = useQuery(["create-create-initial-data"], () => {
		return generalApi
			.getCreateCertData()
			.then((response) => response.data)
			.catch((error) => {
				if (
					axios.isAxiosError(error) &&
					error.response &&
					(error.response.status === 400 || error.response.status === 422)
				) {
					Swal.fire({
						title: "Error",
						text: error.response.data.message || "Something went wrong",
						icon: "error",
						allowOutsideClick: false,
						confirmButtonText: "Back to Home",
					}).then((result) => {
						if (result.isConfirmed) {
							navigate("/profile");
						}
					});
				} else {
					throw error;
				}
			});
	});

	useEffect(() => {
		/**
		 * need to reset the selected courses if user is restart the flow
		 * if session key is set , then we reuse the previous selected courses or they don't have to restart the course selection
		 */
		if (!sessionkey) {
			setSelectedCourses([]);
		}
	}, [sessionkey]);

	useEffect(() => {
		if (!query.data) return;

		let data = query.data;

		/**
		 * help select all courses if user has not selected any courses
		 * and there is no root cause for user to select courses
		 * e.g: for 5 course cert , user only has 5 courses to select from
		 */

		const urlParams = new URLSearchParams(window.location.search);
		const customize = urlParams.get("customize");
		if (!customize) {
			/**
			 * when it is not customize mode, we will select all courses by default
			 * otherwise , just leave it as it is
			 */
			if (selectedCourses.filter(Boolean).length === 0) {
				if (data.courserun_options.length <= Math.max(...data.count_options)) {
					setSelectedCourses(
						data.courserun_options.map((option) => ({
							label: option.label,
							value: option.courserun_id?.toString(),
						})),
					);
				}
			}
		}
	}, [Boolean(query.data)]);

	const countOptions = query.data?.count_options || [];
	const courserunOptions = query.data?.courserun_options || [];

	useEffect(() => {
		/**
		 * if user first land on the page, we will set the selected count based on the selected courses
		 */
		if (countOptions.length > 0 && !sessionkey) {
			if (selectedCount) return;
			if (!countOptions.length) return;

			const maxCount = Math.max(...countOptions);
			setSelectedCount(maxCount);
		}
	}, [courserunOptions]);

	console.log({
		selectedCount,
		countOptions,
		selectedCourses,
		sessionkey,
	});


	const selectionCount = useMemo(() => {
		return selectedCourses.filter(Boolean).length;
	}, [selectedCourses]);

	const { canContinue } = useMemo(() => {
		const maxCount = Math.max(...countOptions);

		if (maxCount == selectionCount) {
			return {
				disableCheckbox: true,
				selectionMessage: "",
				canContinue: true,
			};
		}

		if (countOptions.includes(selectionCount)) {
			return {
				disableCheckbox: false,
				selectionMessage: "",
				canContinue: true,
			};
		} else {
			return {
				disableCheckbox: false,
				selectionMessage: `Select ${countOptions.join(" OR ")} course only`,
				canContinue: false,
			};
		}
	}, [selectionCount, countOptions]);

	const confirmSelectionMutation = useMutation({
		mutationFn: () => {
			if (selectedCourses.filter(Boolean).length < selectedCount) {
				throw new Error("Please select at least " + selectedCount + " courses");
			}

			return generalApi
				.requestProfessionCert({
					courserun_ids: Array.from(selectedCourses.map((course) => course?.value)).filter(Boolean),
				})
				.then(async (data) => {
					await setSessionKey(Date.now().toString());
					let cert_request_id = data.cert_request_id;
					window.scrollTo({ top: 0, left: 0, behavior: "instant" });
					navigate(
						{
							pathname: "/professional-cert/checkout",
							search: `?id=${cert_request_id}`,
						},
						{
							state: {
								cert_request_id,
							},
						},
					);
				});
		},
		onError: (error) => {
			if (axios.isAxiosError(error)) {
				let errorData = error.response.data;
				if (errorData.redirect_url) {
					Swal.fire({
						title: "Error",
						text: errorData.message,
						icon: "error",
						confirmButtonText: errorData.redirect_title || "OK",
						cancelButtonText: "Cancel",
						showCancelButton: true,
						showConfirmButton: true,
					}).then((result) => {
						if (result.isConfirmed) {
							window.location.href = errorData.redirect_url;
						}
					});
				}
			} else {
				Swal.fire({
					title: "Error",
					text: error.message,
					icon: "error",
					confirmButtonText: "OK",
				});
			}
		},
	});

	return (
		<div>
			<TitleContainer pb={0}>
				<ThemedTitle c="#003640" fz={{ base: 25, md: 30 }}>
					Certificate of Professional Competency & <br /> Workplace Excellence
				</ThemedTitle>
			</TitleContainer>

			<Box bg="white">
				<Container maw={1200} mt={20}>
					<div className="row py-4 pb-6 flex-column-reverse flex-md-row">
						<div
							className="col-md-7 pt-lg-6 pl-lg-6 d-flex flex-column gap-1 px-4 px-md-5 mt-4 mt-md-0"
							style={{ minHeight: "100%" }}>
							<Box>
								<Text size="md" fw="bold">
									Please select and confirm the courses to be printed on your professional
									certificate.
								</Text>

								{query.isLoading ? (
									<Center w={"100%"} mih={150}>
										<Spinner />
									</Center>
								) : null}

								<Group mt={20} mb={40}>
									{countOptions.map((currentCount) => {
										const isSelected = String(selectedCount) === String(currentCount);
										const handleClick = () => {
											if (selectedCourses.filter(Boolean).length > currentCount) {
												const exceededCount = selectedCourses.filter(Boolean).length - currentCount;
												openConfirmModal({
													title: "Are you sure you want to select " + currentCount + " courses?",
													children: (
														<Text>
															Are you sure you want to select {currentCount} courses certificate? {exceededCount} courses will be removed.
														</Text>
													),
													onConfirm: () => {
														setSelectedCount(currentCount);
														setSelectedCourses(selectedCourses.filter(Boolean).filter(Boolean).slice(0, currentCount));
													},
													labels: {
														confirm: "Confirm",
														cancel: "Cancel",
													},
												});
											} else {
												setSelectedCount(currentCount);
											}
										};

										return (
											<ThemedButton
												variant={isSelected ? "filled" : 'outline'}
												key={currentCount}
												onClick={handleClick}
												opacity={isSelected ? 1 : 0.5}
											>
												{currentCount} Courses
											</ThemedButton>
										);
									})}
								</Group>

								{Boolean(selectedCount) && (
									<Stack>
										{/* {Array.from({ length: Math.max(...countOptions) }).map((_, index) => { */}
										{Array.from({ length: selectedCount || 0 }).map((_, index) => {
											// workfix or workaround: use moment library to generate ordinal index
											const oridinal = moment.localeData().ordinal(index + 1);
											const currentSelectedCourse = selectedCourses[index];

											const alreadySelected = selectedCourses
												.map((course) => course?.value?.toString())
												.filter(Boolean);

											const availableOption = courserunOptions.filter(
												(course) =>
													!alreadySelected.includes(course.courserun_id?.toString()) ||
													course.courserun_id.toString() === currentSelectedCourse?.value,
											);

											return (
												<Flex align="center" key={index}>
													<Text size="sm" w={50}>
														{index + 1}.
													</Text>
													<Box display={"flex"} flex={"1 1 auto"}>
														<Indicator
															processing
															disabled={Boolean(currentSelectedCourse)}
															w={"100%"}>
															<Select
																clearable
																w={"100%"}
																placeholder={
																	`Select ${oridinal} Course`
																}
																value={currentSelectedCourse?.value || null}
																data={availableOption.map((option) => ({
																	label: option.label,
																	value: option.courserun_id?.toString(),
																}))}
																onChange={(value, option) => {
																	setSelectedCourses((prev) => {
																		const newSelectedCourses = [...prev];
																		newSelectedCourses[index] = option || null;
																		return newSelectedCourses;
																	});
																}}></Select>
														</Indicator>
													</Box>
												</Flex>
											);
										})}
									</Stack>
								)}
							</Box>

							<div className="d-flex justify-content-start flex-column gap-1 mt-4">
								<Flex gap={20} justify={"end"}>
									<LightThemedButton px={{ base: 30, md: 50 }} onClick={() => navigate(-1)}>
										Back
									</LightThemedButton>
									<Indicator processing disabled={!canContinue}>
										<ThemedButton
											disabled={!canContinue || confirmSelectionMutation.isSuccess}
											px={50}
											loading={confirmSelectionMutation.isLoading}
											onClick={() => confirmSelectionMutation.mutate()}>
											Confirm
										</ThemedButton>
									</Indicator>
								</Flex>
							</div>
						</div>
						<Flex
							pos="relative"
							bdrs={20}
							display={"flex"}
							align={"center"}
							justify={"center"}
							direction={"column"}
							className="col-12 col-md-5 col-lg-5 "
							p={20}
							bg="linear-gradient(-30deg, #001932, #00F3BB)">
							<Box pos="relative" w={{ base: "70%", md: "100%" }}>
								{!query.data?.create_professional_cert_url && query.isFetching ? (
									<Skeleton
										animate={true}
										style={{
											aspectRatio: PDF_ASPECT_RATIO,
											width: "100%",
										}}
									/>
								) : (
									<>
										<Image
											className="shadow-lg text-center"
											style={{
												width: "100%",
												aspectRatio: PDF_ASPECT_RATIO,
											}}
											src={query.data?.create_professional_cert_url}
											alt="cert-example"
										/>
										<motion.div
											animate={{
												backgroundPosition: ["-200% 0", "200% 0", "200% 0"],
											}}
											transition={{
												duration: 3,
												delay: 1,
												repeat: 0,
												repeatType: "loop",
											}}
											style={{
												position: "absolute",
												top: 0,
												left: 0,
												width: "100%",
												height: "100%",
												background:
													"linear-gradient(135deg, transparent 0%, transparent 50%, rgba(255, 255, 255, 0.8) 55%, transparent 65%)",
												backgroundSize: "200% 100%",
												zIndex: 10,
												aspectRatio: 1,
											}}
										/>
									</>
								)}
							</Box>
							<Text c="white" size="sm" w="fit-content">
								*Certificate image for illustration purposes only.
							</Text>
						</Flex>
					</div>
				</Container>
			</Box>

			{query.data ? (
				<Container>
					{query.data.ongoing_requests?.length && query.data.existing_certs?.length ? (
						<hr className="mb-8" />
					) : null}

					{query.data.ongoing_requests?.length ? (
						<div>
							<h4>Processing</h4>
							<div className="row">
								{query.data.ongoing_requests.map((request, index) => {
									const shouldBlur = false;
									return (
										<div
											className="col-12 col-md-6 col-lg-4"
											key={index}
											style={{ opacity: shouldBlur ? 0.5 : 1 }}>
											<div className="card p-4 rounded-md d-flex flex-column gap-1">
												<div className="d-flex justify-content-between">
													<Badge color="primary">{request.status}</Badge>
												</div>
												<span>{request.bundle_name}</span>
												<span>
													<i className="fa fa-calendar" /> Requested on {request.date_of_request}
												</span>
												<hr className="my-2" />

												<ul style={{ paddingInlineStart: 10 }}>
													{request.courses.map((courses, index) => {
														return (
															<li key={index}>
																<span>{courses.course_title}</span>
															</li>
														);
													})}
												</ul>
											</div>
										</div>
									);
								})}
							</div>
						</div>
					) : null}

					{query.data.existing_certs?.length ? (
						<div className="mt-6">
							<h4 id="your_cert">Existing Certificate</h4>
							<div className="row">
								{query.data.existing_certs.map((existing_cert_request, index) => {
									/**
									 * blue out if this cert request didn't contain any course
									 * in the user's selected courses
									 */

									let shouldFocus = selectedCourses.length === 0;

									if (selectedCourses.length > 0) {
										/**
										 * check if every of the select course are in the existing cert
										 * currently if so we will focus on the existing cert
										 */
										shouldFocus = selectedCourses.every((selectedCourse) =>
											existing_cert_request.courses.some(
												(course) => course.courserun_id + "" === selectedCourse?.value + "",
											),
										);
									}

									return (
										<div
											className="col-12 col-md-6 col-lg-4 mt-4"
											key={index}
											style={shouldFocus ? {} : { filter: "blur(2px)", opacity: 0.5 }}>
											<Box className="card p-4 rounded-md d-flex flex-column gap-1" bdrs={20}>
												<span>{existing_cert_request.bundle_name}</span>
												<span>
													<i className="fa fa-calendar" /> Completed on{" "}
													{existing_cert_request.cert_date_completed}
												</span>
												<hr className="my-2" />
												<ul style={{ paddingInlineStart: 10 }}>
													{existing_cert_request.courses.map((course, index) => {
														// const isSelected = courserunSelection.get(course.courserun_id);
														const isSelected = selectedCourses.some(
															(selectedCourse) => selectedCourse?.value === course.courserun_id,
														);
														return (
															<li
																key={index}
																style={{ fontWeight: isSelected ? "bold" : "normal" }}>
																<span>{course.course_title}</span>
															</li>
														);
													})}
												</ul>
											</Box>
										</div>
									);
								})}
							</div>
						</div>
					) : null}
				</Container>
			) : null}
		</div>
	);
}